export type Id = string | number;

export type MacroKey = 'p' | 'c' | 'f';
export type NutKey = 'k' | MacroKey;
export type Pct = Record<MacroKey, number>;            // percentages, sum 100
export type Targets = { k: number; p: number; c: number; f: number }; // kcal, grams

export type SlotKey =
  | 'breakfast'
  | 'morning_snacks'
  | 'lunch'
  | 'evening_snacks'
  | 'dinner'
  | string;

export interface Food {
  id?: Id;
  name: string;
  qty: number;              // portions
  unit: string;             // "katori", "piece", "glass"…
  per: [number, number, number, number]; // kcal, protein g, carbs g, fat g per 1 unit
  measureId?: Id;
}

export interface Tip { heading: string; detail: string }

/** Prep tip field limits (desktop/mobile inputs). */
export const TIP_HEADING_MAX_LEN = 150;
export const TIP_DETAIL_MAX_LEN = 200;

export const tipOverLimit = (t: Tip) =>
  t.heading.length > TIP_HEADING_MAX_LEN || t.detail.length > TIP_DETAIL_MAX_LEN;

export const planTipsOverLimit = (plan: Plan) =>
  plan.meals.some(m => m.options.some(o => o.tips.some(tipOverLimit)));

export interface MealOption { id?: Id; foods: Food[]; tips: Tip[] }

export interface Meal {
  key: SlotKey;
  mealTypesId: string;
  name: string;
  time: string;             // start, "08:30"
  endTime: string;          // end, from template API or default window
  share: number;            // % of daily kcal
  ratio: Pct | null;        // null → plan ratio
  ov: Partial<Record<MacroKey, number>>; // gram overrides on the target
  color: string;
  skip: boolean;
  options: MealOption[];
}

export interface Plan {
  id?: Id;                  // draft id once created
  name: string;
  from: string;             // ISO date
  to: string;
  kcal: number;
  pct: Pct;
  /** Which quick-split chip last applied the current pct: Template vs a named preset. */
  macroPreset: string | null;
  templateId: Id | null;
  templateName: string;
  templatePct: Pct | null;
  /** Meal share % from the assigned template (for toggling back from standard split). */
  templateMealShares: number[] | null;
  templateMealRatios: (Pct | null)[] | null;
  /** Start/end times from the assigned template (for toggling back from standard split). */
  templateMealTimes: { start: string; end: string }[] | null;
  split: 'template' | 'standard';
  meals: Meal[];
}

export interface Template {
  id: Id;
  name: string;
  goal: number;
  pct: Pct;
  tag: string;              // "Weight loss" | "Diabetes" | "PCOS" | "Liver" | …
  mine?: boolean;
  shares?: number[];        // per DSLOT, optional (v4)
  ratios?: (Pct | null)[];
  meals: {
    name: string;
    mealTypesId?: string;
    startTime?: string;
    endTime?: string;
    options: { foods: Food[]; tips: Tip[] }[];
  }[];
}

export interface Patient {
  id: Id;
  name: string;
  age: number;
  sex: 'F' | 'M' | string;
  program: string;          // "glp1" | "diabetes" | "pcos" | "liver" …
  programName: string;
  stage?: string;
  weightKg?: number;
  baselineKg?: number;
  heightCm?: number;
}

export interface PlanSummary {
  id: Id;
  name: string;
  status: 'Active' | 'Ended' | 'Draft';
  from: string;
  to: string;
  kcal: number;
}

export type OptionStatus = 'empty' | 'ok' | 'warn' | 'bad';
export interface StatusInfo { st: OptionStatus; label: string; issues: string[] }

export interface KairaRequest {
  patientId: Id;
  planCondition: string;    // template tag / program
  mealSlot: SlotKey;
  mealName: string;
  target: Targets;
  current: Targets;
  foods: { name: string; qty: number; unit: string }[];
}
export interface KairaSuggestion {
  name: string; portion: string; k: number; p: number; c: number; f: number; why: string;
}
export interface KairaResponse { mode: 'add' | 'rich' | 'light'; focus: string; suggestions: KairaSuggestion[] }

export interface AssignBody {
  reason?: string;
  shareWhatsApp: boolean;
  saveTemplate: null | { kind: 'master'; templateId: Id } | { kind: 'mine'; name: string };
}

/** Optional template save choice on assign (preview sheet). */
export type SaveTplMode = 'none' | 'master' | 'copy';

export function resolveSaveTemplate(
  plan: Pick<Plan, 'templateId'>,
  mode: SaveTplMode,
  newTplName: string,
): AssignBody['saveTemplate'] {
  if (mode === 'none') return null;
  if (mode === 'master' && plan.templateId) return { kind: 'master', templateId: plan.templateId };
  if (mode === 'copy') {
    const name = newTplName.trim();
    return name ? { kind: 'mine', name } : null;
  }
  return null;
}

export type BuilderScreen = 'pick' | 'plans' | 'tpl' | 'goals' | 'targets' | 'meals' | 'preview';
export type BuilderSheet = null | 'opt' | 'search' | 'split' | 'assign' | 'previewAssign' | 'leave';
